import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { setTutorialFlag } from "@/tutorialFlag";

const TutorialContext = createContext();

// Helper to get user-specific localStorage key
const getUserKey = (userId, key) => `mealpill_tutorial_${userId}_${key}`;

export function TutorialProvider({ children }) {
  const { user, api } = useAuth();
  const userId = user?.id || "guest";

  // api is read through a ref so goToStep's identity stays stable: goToStep sits in the
  // deps of nextStep (87), the progression effect (166) and RecipePage's tutorial effect
  // (64), so an unstable api would ripple through all three. Mirrors tokenRef in AuthContext.
  const apiRef = useRef(api);
  apiRef.current = api;
  
  const [tutorialActive, setTutorialActive] = useState(false);
  const [tutorialStep, setTutorialStep] = useState(0);
  const [tutorialSkipped, setTutorialSkipped] = useState(false);

  // Single writer for tutorialActive. Mirrors into the module bridge SYNCHRONOUSLY,
  // at the moment the decision is made, rather than from an effect: child effects run
  // before parent effects, so an effect here could fire only after a page had already
  // sent its request. The dangerous direction is a stale TRUE -- a real user action
  // silently dropped from the profile, which is unrecoverable. This makes it impossible.
  const setActive = useCallback((value) => {
    setTutorialFlag(value);
    setTutorialActive(value);
  }, []);
  
  // Track app state for smart step detection
  const [appState, setAppState] = useState({
    hasMealPlan: false,
    hasGroceryItems: false,
    hasCheckedItems: false,
    allItemsChecked: false,
    hasPendingFridgeItems: false,
    hasFridgeItems: false,
    currentPage: "/",
  });

  // For detecting state changes
  const prevAppState = useRef(appState);

  // Initialize tutorial state from user-specific localStorage
  useEffect(() => {
    if (!userId || userId === "guest") return;

    const completed = localStorage.getItem(getUserKey(userId, "completed")) === "true";
    if (completed) {
      setTutorialSkipped(true);
      setActive(false);
      return;
    }
    // Resume an in-progress tutorial if a step was saved
    const savedStep = parseInt(localStorage.getItem(getUserKey(userId, "step")) || "0", 10);
    if (savedStep >= 1) {
      setTutorialStep(savedStep);
      setActive(true);
      setTutorialSkipped(false);
    } else {
      setTutorialSkipped(false);
    }
  }, [userId, setActive]);

  const startTutorial = useCallback(() => {
    if (!tutorialSkipped && userId !== "guest") {
      setActive(true);
      setTutorialStep(1);
      localStorage.setItem(getUserKey(userId, "step"), "1");
    }
  }, [tutorialSkipped, userId, setActive]);

  // Set specific step (for state-based progression)
  const goToStep = useCallback((step) => {
    if (step > 10) {
      if (userId !== "guest") {
        apiRef.current().post("/tutorial/completed", { outcome: "completed", last_step: 10 }).catch(() => {});
      }
      setActive(false);
      localStorage.setItem(getUserKey(userId, "completed"), "true");
      localStorage.removeItem(getUserKey(userId, "step"));
      setTutorialSkipped(true);
      setTutorialStep(0);
    } else if (step >= 1) {
      setTutorialStep(step);
      localStorage.setItem(getUserKey(userId, "step"), String(step));
    }
  }, [userId, setActive]);

  const nextStep = useCallback(() => {
    goToStep(tutorialStep + 1);
  }, [tutorialStep, goToStep]);

  const skipTutorial = useCallback(() => {
    if (userId !== "guest") {
      apiRef.current().post("/tutorial/completed", { outcome: "skipped", last_step: tutorialStep }).catch(() => {});
    }
    setActive(false);
    setTutorialStep(0);
    localStorage.setItem(getUserKey(userId, "completed"), "true");
    localStorage.removeItem(getUserKey(userId, "step"));
    setTutorialSkipped(true);
  }, [userId, setActive, tutorialStep]);

  const resetTutorial = useCallback(() => {
    localStorage.removeItem(getUserKey(userId, "completed"));
    localStorage.removeItem(getUserKey(userId, "step"));
    setTutorialSkipped(false);
    setTutorialStep(0);
    setActive(false);
  }, [userId, setActive]);

  // Update app state - called from pages
  const updateAppState = useCallback((updates) => {
    setAppState((prev) => ({ ...prev, ...updates }));
  }, []);

  // Detect current step based on app state (for resume capability)
  const detectCurrentStep = useCallback(() => {
    const { hasMealPlan, hasGroceryItems, hasCheckedItems, hasPendingFridgeItems, hasFridgeItems, currentPage } = appState;
    
    // Logic: find the first incomplete step based on state
    if (!hasMealPlan) return 1;
    if (currentPage === "/planner" && hasMealPlan) return 2;
    if (currentPage.startsWith("/recipe") && !hasGroceryItems) return 3;
    if (hasGroceryItems && !hasCheckedItems) return 4;
    if (hasCheckedItems && !hasPendingFridgeItems && currentPage === "/grocery") return 5;
    if (hasPendingFridgeItems) return 6;
    if (currentPage === "/fridge" && !hasPendingFridgeItems && hasFridgeItems) return 7;
    if (hasFridgeItems && currentPage === "/") return 8;
    if (currentPage.startsWith("/recipe") && hasFridgeItems) return 9;
    
    return tutorialStep || 1;
  }, [appState, tutorialStep]);

  // State-based step progression
  useEffect(() => {
    if (!tutorialActive) return;
    
    const prev = prevAppState.current;
    const curr = appState;
    
    // Detect state transitions and auto-advance
    // Step 1->2: Meal plan generated
    if (tutorialStep === 1 && !prev.hasMealPlan && curr.hasMealPlan) {
      goToStep(2);
    }
    // Step 2->3: User clicked on a meal (navigated to recipe)
    else if (tutorialStep === 2 && curr.currentPage.startsWith("/recipe")) {
      goToStep(3);
    }
    // Step 3->4: Handled in RecipePage.js when addMissingToCart is clicked
    // Step 4->5: User arrived at the grocery/cart page
    else if (tutorialStep === 4 && curr.currentPage === "/grocery") {
      goToStep(5);
    }
    // Step 5->6: Handled in GroceryPage when handlePurchase is clicked
    // Step 6->7: User clicked fridge nav (navigated to fridge)
    else if (tutorialStep === 6 && curr.currentPage === "/fridge" && curr.hasPendingFridgeItems) {
      goToStep(7);
    }
    // Step 7->8: Handled in FridgePage when confirmPending is clicked
    // Step 8->9: User navigated back to home
    else if (tutorialStep === 8 && curr.currentPage === "/") {
      goToStep(9);
    }
    // Step 9->10: User clicked on recipe from home
    else if (tutorialStep === 9 && curr.currentPage.startsWith("/recipe")) {
      goToStep(10);
    }
    // Step 10->complete: Handled in RecipePage when markDone is called
    
    prevAppState.current = curr;
  }, [appState, tutorialActive, tutorialStep, goToStep]);

  return (
    <TutorialContext.Provider
      value={{
        tutorialActive,
        tutorialStep,
        tutorialSkipped,
        appState,
        startTutorial,
        nextStep,
        goToStep,
        skipTutorial,
        resetTutorial,
        updateAppState,
        detectCurrentStep,
      }}
    >
      {children}
    </TutorialContext.Provider>
  );
}

export const useTutorial = () => useContext(TutorialContext);
